<template>
    <v-container fluid>
        <v-row align="start" justify="space-between" class="dddark pt-6 px-1" style="margin-top: -10px;">

            <v-col cols="12" sm="12" md="4" class="mr-auto rounded">
                <ClinicSelector @selected="updateData" @cleared="updateData" :default="clinic" :elevation="this.isAdmin && !this.clinic ? 15 : 0"/>
            </v-col>

            <v-col cols="auto" v-if="!isMobile" v-show="activeProductTag === 'ai_chat'">
                <v-btn
                    outlined
                    elevation="0"
                    @click="downloadReport"
                    :disabled="loading"
                    style="margin-top: 1px; border-color: var(--v-ddgrey-base )">
                    <v-icon color="ddgrey">mdi-download</v-icon>
                </v-btn>
            </v-col>
            <v-col v-if="this.clinicLocations.length" cols="12" sm="auto" v-show="activeProductTag === 'ai_chat'">
                <LocationSelector
                    :loading="loading"
                    :locations="this.clinicLocations"
                    :initial="this.currentClinicLocation"
                    @selected="locationChanged"
                    @cleared="locationChanged(false)"/>
            </v-col>
            <v-col cols="12" sm="auto" v-show="activeProductTag === 'ai_chat'">
                <DateRangePicker
                    :loading="loading"
                    @selected="datesChanged"
                    @cleared="datesChanged(false)"/>
            </v-col>
        </v-row>

        <EmptyDashboard v-if="this.isAdmin && !this.clinic" @selected="updateData" />

        <v-row v-if="this.clinic" class="ddblack">
            <ProductsAppBar v-if="this.products.length > 1" :products="products" :absolute="true" />

            <v-container fluid>
                <v-tabs-items v-model="activeProductTag" :touchless="true">
                    <v-tab-item value="ai_chat" href="ai_chat">
                        <AIChatDataReport :clinic="this.clinic" :account="productsMetadata.account_id" :data="data" :loading="loading" :dateFilter="this.dateFilter" />
                    </v-tab-item>
                    <v-tab-item value="reviews" href="reviews">
                        <ReviewsDashboard :initial-clinic="this.clinic" :ghl-key="productsMetadata.ghl_key" :account-id="productsMetadata.account_id"></ReviewsDashboard>
                    </v-tab-item>
                    <v-tab-item value="website" href="website">
                        <WebsitesDashboard :property-key="Number(productsMetadata.ga_property_key)" :property-name="this.clinic">
                        </WebsitesDashboard>
                    </v-tab-item>
                    <v-tab-item value="webmd" href="webmd">
                        <WebMDDashboard :initial-clinic="this.clinic"></WebMDDashboard>
                    </v-tab-item>
                    <v-tab-item value="text_to_pay" href="text_to_pay">
                        <TextToPayDashboard :initial-clinic="this.clinic" :ghl-key="productsMetadata.ghl_key"></TextToPayDashboard>
                    </v-tab-item>
                    <v-tab-item value="ai_dialer" href="ai_dialer">
                        <AIAutoDialerDashboard :initial-clinic="this.clinic"></AIAutoDialerDashboard>
                    </v-tab-item>
                    <v-tab-item value="appointments" href="appointments">
                        <AppointmentsDashboard :initial-clinic="this.clinic" :location-id="productsMetadata.ghl_key"></AppointmentsDashboard>
                    </v-tab-item>
                </v-tabs-items>
            </v-container>
        </v-row>
    </v-container>
</template>

<script>
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import Vue from 'vue';
import VueIntercom from 'vue-intercom';

import DataAPI from '@/utils/DataAPI';
import AuthAPI from '@/utils/AuthAPI';
import DashboardTrackingAPI from '@/utils/DashboardTrackingAPI';
import EventBus from '@/utils/EventBus';
import CustomGA from '@/utils/CustomGA';

import ClinicSelector from '@/components/dashboard/controls/ClinicSelector';
import LocationSelector from '@/components/dashboard/controls/LocationSelector';
import DateRangePicker from '@/components/dashboard/controls/DateRangePicker';

import ProductsAppBar from "@/components/appbar/ProductsAppBar.vue";
import EmptyDashboard from '@/components/dashboard/widgets/EmptyDashboard';
import AIChatDataReport from '@/components/dashboard/aiChat/AIChatDataReport';
import ReviewsDashboard from "@/views/ReviewsDashboard.vue";
import WebMDDashboard from "@/views/WebMDDashboard.vue";
import WebsitesDashboard from "@/views/WebsitesDashboard.vue";
import TextToPayDashboard from "@/views/TextToPayDashboard.vue";
import AIAutoDialerDashboard from "@/views/AIAutoDialerDashboard.vue";
import AppointmentsDashboard from "@/views/AppointmentsDashboard.vue";

import { ACTIVE_PRODUCTS } from "@/utils/constants";

Vue.use(VueIntercom, { app_id: 'qda6wwyr' });


/**
 * This component includes all the features of Dashboard page such as change
 * between clinics, dates, download the report and show all other dashboard
 * widgets.
 *
 * To get data from the backend it uses the class DataAPI. To ensure that the
 * user is logged and check its capabilities it uses the class AuthAPI.
 */

export default {
    name: 'Dashboard',
    data: () => ({
        loading: true,
        rawData: {},
        clinic: "",
        account: "",
        clinicLocations: [],
        currentClinicLocation: 'all',
        data: {
            chats: {},
            existingVsNewOnes: {},
            stats: {
                averageTimeSpent: 0,
                conversionRate: 0,
                numberOfChats: {
                    total: 0,
                    last_month: 0
                },
                numberOfReferrals: {
                    total: 0,
                    last_month: 0
                },
                referralGrowth: 0,
                newPatients: 0
            },
            questions: {},
            locations: {
                locations: [],
                topLocations: []
            },
            devices: {},
            mostCommonlyQuestion: ''
        },
        dateFilter: {
            from: false,
            to: false
        },
        isMobile: false,
        appVersion: [],
        isAdmin: false,
        products: [],
        productsMetadata: {
            account_id: "",
            webmd_npi: 0,
            ga_property_key: 0,
            ghl_key: ""
        },
        activeTab: 0,
        activeProductTag: "ai_chat",
    }),
    async mounted() {
        this.onResize();
        window.addEventListener('resize', this.onResize);

        const [ success, user ] = await AuthAPI.currentUser();
        this.isAdmin = user.isAdmin;
        this.appVersion = user.app_version;

        if (!success) this.$router.push({ name: 'login' });

        this.trackLogin(user);
        console.log(user);
        // this.initAnalytics(user);
        CustomGA.init(user);
        this.loadIntercom(user);

        EventBus.$on("products-app-bar-clicked", (product, tabIndex) => {
            console.log(`Customer clicked on tab ${product}, ${tabIndex}`)
            this.activeTab = tabIndex;
            this.activeProductTag = product;
        });

        if (this.isAdmin) await this.updateData();
        else if (user.attributes['clinics']) await this.updateData(user.attributes.clinics[0]);
        else this.$router.push({ name: 'login' });
    },
    beforeDestroy() {
        if (this.$intercom) this.$intercom.shutdown();
    },
    methods: {
        trackLogin(user) {
            const clinicName = (user.attributes && user.attributes.clinics && user.attributes.clinics.length > 0) ? user.attributes.clinics[0]: null;
            DashboardTrackingAPI.trackLogin(user.attributes.email, user.username, clinicName);
        },
        loadIntercom(user){
            this.$intercom.boot({
                app_id: 'qda6wwyr',
                user_id: user.username,
                name: user.attributes.name,
                email: user.attributes.email,
            });
        },
        initAnalytics(user) {
            const user_id = user.username;
            const app_name = 'ReportApp';
            const groups = AuthAPI.appVersions.join(',');
            window.gtag('config', 'GA_MEASUREMENT_ID', { user_id, app_name, groups });
        },
        formatDateFilter() {
            const format = (date) => `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
            if (this.dateFilter.from && this.dateFilter.to && this.dateFilter.from !== this.dateFilter.to) {
                return `${format(this.dateFilter.from)} - ${format(this.dateFilter.to)}`;
            } else if (this.dateFilter.from) {
                return format(this.dateFilter.from);
            } else return 'all time';
        },
        resetData() {
            this.clinic = '';
            this.account = '';
            this.data = {
                chats: {},
                existingVsNewOnes: {},
                stats: {
                    averageTimeSpent: 0,
                    conversionRate: 0,
                    numberOfChats: {
                        total: 0,
                        last_month: 0
                    },
                    numberOfReferrals: {
                        total: 0,
                        last_month: 0
                    },
                    referralGrowth: 0,
                    newPatients: 0
                },
                questions: {},
                locations: {
                    locations: [],
                    topLocations: []
                },
                devices: {},
                mostCommonlyQuestion: ''
            };
            this.products = [];
            this.productsMetadata = {
                account_id: "",
                webmd_npi: 0,
                ga_property_key: 0,
                ghl_key: ""
            };
            this.activeTab = 0;
            this.activeProductTag = "ai_chat";
        },
        async updateData(clinic = false, account = false, location = false) {
            this.loading = true;
            this.resetData();
            if (!clinic && !account) {
                this.clinic = '';
                return;
            }
            this.clinic = clinic;
            // window.gtag('event', 'chat_selection_changed', { value: clinic });
            window.GA.trackEvent('chat_selection_changed', this.clinic);
            await this.getActiveProducts();
            await this.getClinicData(clinic, this.productsMetadata.account_id, location);
            this.loading = false;
        },
        async getActiveProducts() {
            try {
                const productsConfig = await DataAPI.getActiveProducts(this.clinic.trim());
                this.products = productsConfig?.products ?? [];
                this.productsMetadata = productsConfig?.metadata ?? {};
                if (this.products.length > 0) {
                    this.products = this.products.sort();
                    this.products = this.products.filter(product => {
                        // don't show TextToPay if GHL key is not found (Mongo path needs it)
                        if (!this.productsMetadata.ghl_key && product === 'text_to_pay') {
                            return false;
                        }
                        // don't show Websites if GA Property Key is not found
                        if (!this.productsMetadata.ga_property_key && product === 'website') {
                            return false;
                        }
                        return ACTIVE_PRODUCTS.includes(product)
                    });
                }
            } catch (e) {
                console.error(e);
                EventBus.$emit('Error getting active products data');
            }
        },
        async getClinicData(clinic = false, account = false, location = false) {
            try {
                const { from, to } = this.dateFilter;
                console.log(clinic, account, location);
                this.data = await DataAPI.getData({ clinic, account, location, from, to });

                this.clinic = this.data.clinic;
                this.clinicLocations = this.data.clinicLocations;
                this.currentClinicLocations = this.data.currentClinicLocations;
            } catch (e) {
                console.error(e);
                EventBus.$emit('Error getting clinic data');
            }
        },
        async datesChanged(dates) {
            if (dates && dates.length) window.GA.trackEvent('date_filter_changed', dates.join('  ~  '));
            // if (dates && dates.length) window.gtag('event', 'date_filter_changed', { value: dates.join('  ~  ') });

            let from, to;
            if (Array.isArray(dates)) {
                [ from, to ] = dates.length === 1 ? [ dates[0], dates[0] ] : dates;
            } else [ from, to ] = [ false, false ];
            this.dateFilter = { from, to };
            await this.updateData(this.clinic);
        },
        async locationChanged(location) {
            if (location) window.GA.trackEvent('location_selectpr_changed', location);
            // if (location) window.gtag('event', 'location_selectpr_changed', { value: location });
            await this.updateData(this.clinic, location);
        },
        async downloadReport() {
            EventBus.$emit("popup", "Downloading...");

            const reportElement = document.getElementById('report');
            const filename = `${ this.clinic } (${ this.formatDateFilter() }).pdf`;
            // window.gtag('event', 'report_download', { value: filename });
            window.GA.trackEvent('report_download', filename);

            const canvas = await html2canvas(reportElement);
            const img = canvas.toDataURL('image/png');

            const doc = new jsPDF({
                unit: 'px',
                format: [ canvas.width, canvas.height ],
                hotfixes: ["px_scaling"]
            });

            doc.addImage(img, 'PNG', 0, 0);
            doc.save(filename);
        },
        onResize() {
            this.isMobile = window.innerWidth < 1000;
        }
    },
    components: {
        EmptyDashboard,
        ClinicSelector,
        LocationSelector,
        DateRangePicker,
        ProductsAppBar,
        AIChatDataReport,
        WebsitesDashboard,
        WebMDDashboard,
        ReviewsDashboard,
        TextToPayDashboard,
        AIAutoDialerDashboard,
        AppointmentsDashboard,
    }
}
</script>
